import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FillBar — the plain-english meter for how close Sage is to the $20M ceiling.
 * Above the ceiling, revenue splits four ways: 50% to active users,
 * 25% to Resilience parent, 20% to homeless / disabled / in-crisis / disaster relief, 5% to whistleblowers.
 * Written for a non-coder audience.
 */
export default function FillBar() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/sage/fill-bar`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return (
      <div data-testid="fill-bar-error" className="border border-sage-warn p-3 text-xs text-sage-warn">
        Fill Bar unavailable · {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid="fill-bar-loading" className="border border-sage-border p-6 text-xs text-sage-faint">
        Loading Fill Bar…
      </div>
    );
  }

  const pct = Math.min(100, Math.max(0.0001, data.ceiling_pct || 0));
  const barPct = pct < 0.1 ? 0.1 : pct; // keep the bar visible even at $0

  return (
    <div data-testid="fill-bar" className="border border-sage-border p-6 bg-sage-elevated">
      <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint mb-3">
        The Fill Bar · $20M ceiling meter
      </div>

      <div className="flex items-baseline gap-3 flex-wrap mb-3">
        <div className="text-4xl md:text-5xl font-bold tabular-nums text-sage-accent" data-testid="fill-bar-revenue">
          ${(data.revenue_usd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className="text-sage-muted tabular-nums">
          / ${(data.ceiling_usd ?? 0).toLocaleString()} ceiling
        </div>
        <div className="text-sage-warn tabular-nums font-bold" data-testid="fill-bar-pct">
          {(data.ceiling_pct ?? 0).toFixed(6)}%
        </div>
      </div>

      <div
        className="w-full h-4 border border-sage-border bg-sage-bg relative"
        title={`${(data.ceiling_pct ?? 0).toFixed(4)}% of the ceiling`}
        data-testid="fill-bar-meter"
      >
        <div
          className="h-full bg-sage-accent transition-all duration-500"
          style={{ width: `${barPct}%` }}
        />
      </div>

      <div className="mt-3 text-[11px] text-sage-faint tabular-nums flex flex-wrap gap-x-6 gap-y-1">
        <span>Distance: ${(data.distance_to_ceiling_usd ?? 0).toLocaleString()}</span>
        <span>Active members: {data.active_members ?? 0}</span>
        <span>Above ceiling → {Math.round((data.split_above_ceiling?.to_users_pct || 0) * 100)}% users / {Math.round((data.split_above_ceiling?.homeless_disabled_pct || 0) * 100)}% homeless+disabled / {Math.round((data.split_above_ceiling?.disaster_relief_pct || 0) * 100)}% disaster / {Math.round((data.split_above_ceiling?.whistleblower_pct || 0) * 100)}% refuge</span>
      </div>

      <p className="mt-4 text-sm text-sage-muted leading-relaxed sage-prose" data-testid="fill-bar-letter">
        {data.letter}
      </p>
    </div>
  );
}
